/**
 * Rate Limiter - Previene abuso de la API
 */

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
}

interface RateLimitEntry {
  count: number;
  resetAt: number;
}

class RateLimiter {
  private limits: Map<string, RateLimitEntry> = new Map();
  private config: RateLimitConfig;

  constructor(config: RateLimitConfig = { maxRequests: 60, windowMs: 60000 }) {
    this.config = config;
  }

  /**
   * Verifica si una acción está dentro del límite
   * @param key - Identificador único (ej: 'api:clientes:delete')
   * @returns true si permitido, false si excede límite
   */
  check(key: string): boolean {
    const now = Date.now();
    const entry = this.limits.get(key);

    if (!entry || now > entry.resetAt) {
      this.limits.set(key, {
        count: 1,
        resetAt: now + this.config.windowMs,
      });
      return true;
    }

    if (entry.count >= this.config.maxRequests) {
      return false;
    }

    entry.count++;
    return true;
  }

  /**
   * Obtiene el tiempo restante hasta reset
   */
  getTimeUntilReset(key: string): number {
    const entry = this.limits.get(key);
    if (!entry) return 0;
    return Math.max(0, entry.resetAt - Date.now());
  }

  /**
   * Limpia entradas expiradas
   */
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.limits.forEach((entry, key) => {
      if (now > entry.resetAt) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.limits.delete(key));
  }
}

// Rate limiters por tipo de acción
export const apiRateLimiter = new RateLimiter({ maxRequests: 60, windowMs: 60000 });
export const authRateLimiter = new RateLimiter({ maxRequests: 5, windowMs: 300000 }); // 5 intentos en 5 min
export const deleteRateLimiter = new RateLimiter({ maxRequests: 10, windowMs: 60000 }); // 10 deletes por minuto
export const createRateLimiter = new RateLimiter({ maxRequests: 30, windowMs: 60000 }); // 30 creates por minuto

/**
 * Hook para usar rate limiting
 */
export function useRateLimit(limiter: RateLimiter, key: string): {
  canProceed: boolean;
  timeUntilReset: number;
} {
  const canProceed = limiter.check(key);
  const timeUntilReset = limiter.getTimeUntilReset(key);
  return { canProceed, timeUntilReset };
}

/**
 * Decorator para funciones con rate limit
 */
export function withRateLimit<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  limiter: RateLimiter,
  key: string
): T {
  return (async (...args: unknown[]) => {
    if (!limiter.check(key)) {
      throw new Error('Rate limit exceeded. Please wait before trying again.');
    }
    return fn(...args);
  }) as T;
}
